import MicIcon from '@mui/icons-material/Mic';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { type FC, useEffect } from 'react';

export interface MicrophoneDevice {
  deviceId: string;
  label: string;
  groupId: string;
}

interface MicrophoneSelectorProps {
  selectedDeviceId: string;
  onDeviceChange: (deviceId: string) => void;
  devices: MicrophoneDevice[];
}

/**
 * Reusable microphone selector component
 *
 * @param props - Component props
 * @returns Microphone selector component
 */
const MicrophoneSelector: FC<MicrophoneSelectorProps> = ({ selectedDeviceId, onDeviceChange, devices }) => {
  const handleDeviceChange = (event: SelectChangeEvent<string>) => {
    const deviceId = event.target.value;
    onDeviceChange(deviceId);
  };

  // Auto-select first device if none selected and devices are available
  useEffect(() => {
    if (!selectedDeviceId && devices.length > 0) {
      onDeviceChange(devices[0].deviceId);
    }
  }, [devices, selectedDeviceId, onDeviceChange]);

  if (devices.length === 0) {
    return (
      <Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 1,
          }}
        >
          No se encontraron dispositivos de audio.
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          • Asegúrate de que tu micrófono esté conectado y funcionando
          <br />• Verifica que el navegador tenga permisos para acceder al micrófono
          <br />• Intenta refrescar la página o reiniciar el navegador
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel id="microphone-select-label">Micrófono</InputLabel>

        <Select
          labelId="microphone-select-label"
          id="microphone-select"
          value={selectedDeviceId || ''}
          label="Micrófono"
          onChange={handleDeviceChange}
          startAdornment={<MicIcon sx={{ mr: 1, color: 'action.active' }} />}
          displayEmpty
        >
          {devices.map((device) => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
              <Typography variant="body2">{device.label}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {devices.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 1,
            display: 'block',
          }}
        >
          {devices.length} micrófono{devices.length !== 1 ? 's' : ''} disponible
        </Typography>
      )}
    </Box>
  );
};

export default MicrophoneSelector;
